/**
 * LotSmith — Dashboard shell.
 *
 * Layout:
 *   Desktop (≥768px): top nav with account selector + tier pill + email.
 *   Mobile (<768px):  top bar with account selector, bottom tab nav.
 *
 * Provider selection (M12):
 *   - `?state=` URL param → DevProvider (preview / QA harness)
 *   - VITE_LICENSE_API_BASE configured + signed-in (`/v1/auth/me` 200) →
 *     LiveProvider (real D1 reads)
 *   - Anything else → DevProvider (offline / local dev)
 */
import { useEffect, useState, type ReactNode } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";

import { DevProvider } from "../providers/DevProvider.js";
import { LiveProvider } from "../providers/LiveProvider.js";
import { useDev, type DevUser } from "../providers/dataContext.js";
import { DemoBanner } from "../components/DemoBanner.js";

export function DashboardShell() {
  return (
    <DataProviderRouter>
      <div className="app">
        <TopBar />
        <DemoBanner />
        <main className="app__main">
          <Outlet />
        </main>
        <BottomNav />
      </div>
    </DataProviderRouter>
  );
}

type Mode = "deciding" | "dev" | "live";

function DataProviderRouter({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<Mode>("deciding");
  const [user, setUser] = useState<DevUser | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stateOverride = params.get("state");
    if (stateOverride) {
      setMode("dev");
      return;
    }
    const license = licenseApiBase();
    const auth = authApiBase();
    if (!license) {
      setMode("dev");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${auth}/v1/auth/me`, {
          credentials: "include",
          headers: { accept: "application/json" },
        });
        if (cancelled) return;
        if (!r.ok) {
          // Not signed in (401) or auth worker unreachable — fall back to demo
          // so /app remains explorable. M3 onboarding handles the redirect to
          // /login when the user actually tries to take an action.
          setMode("dev");
          return;
        }
        const j = (await r.json()) as { email: string; tier: DevUser["tier"] };
        setUser({ email: j.email, tier: j.tier });
        setMode("live");
      } catch {
        if (!cancelled) setMode("dev");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (mode === "deciding") {
    return (
      <div className="app">
        <main className="app__main" aria-busy>
          <div style={{ padding: 24 }}>Loading…</div>
        </main>
      </div>
    );
  }
  if (mode === "live" && user) {
    return (
      <LiveProvider apiBase={licenseApiBase()} user={user}>
        {children}
      </LiveProvider>
    );
  }
  return <DevProvider>{children}</DevProvider>;
}

function licenseApiBase(): string {
  return (import.meta.env.VITE_LICENSE_API_BASE as string | undefined) ?? "";
}

function authApiBase(): string {
  return (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
}

function TopBar() {
  const { user, accounts, activeAccountId, setActiveAccountId } = useDev();
  const active = accounts.find((a) => a.id === activeAccountId) ?? accounts[0];
  // Surface a state dot next to the account selector when this user has any
  // account in a non-NORMAL state. Multi-account traders can be on /app
  // viewing a healthy account while another is BLOCKED — the dot makes that
  // visible at a glance.
  const worstState = pickWorstState(accounts.map((a) => a.eaState));
  return (
    <header className="app-top">
      <div className="app-top__inner">
        <Link to="/app" className="app-top__brand">LotSmith</Link>
        <div className="app-top__account">
          {accounts.length > 0 ? (
            <div className="acct-select-wrap">
              {worstState && (
                <span
                  className={`acct-pill acct-pill--${stateTone(worstState)}`}
                  title={`At least one account is ${worstState}`}
                  aria-label={`Account state: ${worstState}`}
                />
              )}
              <select
                className="acct-select"
                value={active?.id ?? ""}
                onChange={(e) => setActiveAccountId(e.target.value)}
                aria-label="Select account"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {accountLabel(a)}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <span className="app-top__no-acct">No accounts</span>
          )}
        </div>
        <nav className="app-top__nav" aria-label="Primary">
          <NavLink to="/app" end className={topNavClass}>Overview</NavLink>
          <NavLink to="/app/trades" className={topNavClass}>Trades</NavLink>
          <NavLink to="/app/calendar" className={topNavClass}>Calendar</NavLink>
          <NavLink to="/app/settings" className={topNavClass}>Settings</NavLink>
        </nav>
        <div className="app-top__user">
          <span className={`app-top__tier app-top__tier--${user.tier.toLowerCase()}`}>{user.tier}</span>
          <span className="app-top__email">{user.email}</span>
        </div>
      </div>
    </header>
  );
}

function accountLabel(a: { propFirm: string; phase: string; brokerLogin: string; eaState: import("../providers/dataContext.js").EaState | null }): string {
  const base = `${a.propFirm} · ${a.phase} · #${a.brokerLogin}`;
  if (!a.eaState || a.eaState === "NORMAL") return base;
  return `${base} — ${a.eaState}`;
}

const STATE_PRIORITY: Record<string, number> = {
  BLOCKED: 5, TARGET_HIT: 4, OVER_LIMIT: 3, COOLDOWN: 2, ALGO_OFF: 1, NORMAL: 0,
};

function pickWorstState(states: Array<import("../providers/dataContext.js").EaState | null>): import("../providers/dataContext.js").EaState | null {
  let worst: import("../providers/dataContext.js").EaState | null = null;
  let worstRank = 0;
  for (const s of states) {
    if (!s || s === "NORMAL") continue;
    const rank = STATE_PRIORITY[s] ?? 0;
    if (rank > worstRank) { worst = s; worstRank = rank; }
  }
  return worst;
}

function stateTone(s: import("../providers/dataContext.js").EaState): "danger" | "warning" | "success" {
  if (s === "BLOCKED") return "danger";
  if (s === "TARGET_HIT") return "success";
  return "warning";
}

function BottomNav() {
  return (
    <nav className="bottom-nav" aria-label="Primary">
      <NavLink to="/app" end className={tabClass}>
        <span aria-hidden className="bottom-nav__icon">▦</span>
        <span>Overview</span>
      </NavLink>
      <NavLink to="/app/trades" className={tabClass}>
        <span aria-hidden className="bottom-nav__icon">⇅</span>
        <span>Trades</span>
      </NavLink>
      <NavLink to="/app/calendar" className={tabClass}>
        <span aria-hidden className="bottom-nav__icon">▤</span>
        <span>Calendar</span>
      </NavLink>
      <NavLink to="/app/settings" className={tabClass}>
        <span aria-hidden className="bottom-nav__icon">⚙</span>
        <span>Settings</span>
      </NavLink>
    </nav>
  );
}

function tabClass({ isActive }: { isActive: boolean }): string {
  return `bottom-nav__tab${isActive ? " is-active" : ""}`;
}

function topNavClass({ isActive }: { isActive: boolean }): string {
  return `app-top__navlink${isActive ? " is-active" : ""}`;
}
